import { z } from 'zod';
import { SynthesisFileInputSchema } from './plan';

/**
 * Request body for POST /api/synthesis/submit-batch.
 *
 * cabinetId is INTENTIONALLY ABSENT. It is sourced server-side from
 * req.user.cabinetId (injected by authMiddleware from cabinet_member).
 * This enforces the multi-tenant boundary at the route, preventing
 * cross-cabinet enqueue attempts by construction.
 */
export const SubmitSynthesisBatchRequestSchema = z.object({
  caseId: z.string().uuid(),
  files: z.array(SynthesisFileInputSchema).min(1).max(10),
}).refine(
  (data) => data.files.every(f => f.dossierID === data.caseId),
  {
    message: 'All files in a batch must share the same caseId as the request-level caseId',
    path: ['files'],
  }
);

export type SubmitSynthesisBatchRequest = z.infer<typeof SubmitSynthesisBatchRequestSchema>;

/**
 * A single upload can exceed the 2,000 source-document processing limit.
 * Accept the full payload so the backend can persist those documents as held,
 * while retaining a finite request-size guard.
 */
export const MAX_AI_PROCESSING_FINALIZE_DOCUMENTS = 5000;

export const AiProcessingFinalizeRequestSchema = z.object({
  documentIds: z.array(z.string().uuid()).min(1).max(MAX_AI_PROCESSING_FINALIZE_DOCUMENTS),
  caseMetadata: z.object({
    dossierName: z.string().trim().min(1),
    clientName: z.string().optional(),
    lawyerName: z.string().optional(),
    typeDeProcedure: z.string().optional(),
  }),
  generateTitle: z.boolean(),
});

export const AiProcessingReleaseRequestSchema = AiProcessingFinalizeRequestSchema.omit({ documentIds: true });

export const AiProcessingSummaryResponseSchema = z.object({
  success: z.literal(true),
  aiProcessing: z.object({
    limit: z.literal(2000),
    totalSourceDocuments: z.number().int().nonnegative(),
    heldSourceDocumentsCount: z.number().int().nonnegative(),
    eligibleForRelease: z.boolean(),
  }),
});

export const AiProcessingGateResponseSchema = z.discriminatedUnion('status', [
  z.object({
    success: z.literal(true),
    status: z.literal('held'),
    limit: z.literal(2000),
    totalSourceDocuments: z.number().int().nonnegative(),
    heldDocumentIds: z.array(z.string().uuid()),
    message: z.string(),
  }),
  z.object({
    success: z.literal(true),
    status: z.literal('submitted'),
    limit: z.literal(2000),
    totalSourceDocuments: z.number().int().nonnegative(),
    submittedDocumentIds: z.array(z.string().uuid()),
    jobIds: z.array(z.string().uuid()),
  }),
]);

export type AiProcessingFinalizeRequest = z.infer<typeof AiProcessingFinalizeRequestSchema>;
export type AiProcessingReleaseRequest = z.infer<typeof AiProcessingReleaseRequestSchema>;
export type AiProcessingSummaryResponse = z.infer<typeof AiProcessingSummaryResponseSchema>;
export type AiProcessingGateResponse = z.infer<typeof AiProcessingGateResponseSchema>;
